import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';

import { textoDeAjuda } from '@/shared/help/ajudaDosCampos';
import { cn } from '@/shared/lib/cn';

export interface FieldLabelProps {
  htmlFor: string;
  label: string;
  /**
   * Identificador do proprio rotulo, para o controle que nao e campo de formulario
   * (a area editavel do texto rico) se ligar a ele por aria-labelledby.
   */
  id?: string | undefined;
  className?: string | undefined;
  /**
   * Texto do icone de ajuda. Sem ele, o campo procura a propria ajuda no
   * dicionario pela tela e pelo rotulo; `false` desliga a ajuda.
   */
  help?: string | false | undefined;
}

/**
 * Rotulo do campo, com o icone de ajuda (i) quando ha texto para ele.
 *
 * O icone e um botao, e nao um texto que aparece ao passar o mouse: quem usa
 * teclado ou toque tambem precisa chegar a ajuda.
 */
export function FieldLabel({ htmlFor, label, id, className, help }: FieldLabelProps) {
  const rotulo = (
    <label id={id} htmlFor={htmlFor} className={className}>
      {label}
    </label>
  );

  if (help === false) return rotulo;
  if (typeof help === 'string') {
    return <ComAjuda rotulo={rotulo} label={label} texto={help} />;
  }
  return <AjudaDoDicionario rotulo={rotulo} label={label} />;
}

/**
 * Ajuda (i) ao lado de um texto que nao e rotulo de campo, como o titulo de uma
 * coluna (a "Vigencia Atual" da lista de apolices do OnPoint). Mesmo botao e mesmo
 * balao do campo.
 */
export function HelpTip({ label, text }: { label: string; text: string }) {
  return <ComAjuda rotulo={<span>{label}</span>} label={label} texto={text} emLinha />;
}

interface AjudaDoDicionarioProps {
  rotulo: ReactElement;
  label: string;
}

// O componente que le a rota so existe dentro do roteador; o campo testado sozinho
// fica com o dicionario geral. Dois componentes, e nao um gancho condicional,
// porque a ordem dos ganchos nao pode depender de onde o campo foi montado.
function AjudaDoDicionario({ rotulo, label }: AjudaDoDicionarioProps) {
  if (useInRouterContext()) return <AjudaDaRota rotulo={rotulo} label={label} />;
  const texto = textoDeAjuda('', label);
  return texto ? <ComAjuda rotulo={rotulo} label={label} texto={texto} /> : rotulo;
}

function AjudaDaRota({ rotulo, label }: AjudaDoDicionarioProps) {
  const { pathname } = useLocation();
  const texto = textoDeAjuda(pathname, label);
  return texto ? <ComAjuda rotulo={rotulo} label={label} texto={texto} /> : rotulo;
}

interface ComAjudaProps {
  rotulo: ReactElement;
  label: string;
  texto: string;
  /**
   * O titulo de coluna segue o alinhamento da celula (a coluna de numero alinha a
   * direita); o rotulo de campo ocupa a linha dele.
   */
  emLinha?: boolean | undefined;
}

/**
 * O navegador desenha o `popover` na camada de cima. Onde ele nao existe (navegador
 * antigo, ambiente de teste), o atributo ficaria so escondendo o balao: sem ele, o
 * mesmo balao aparece com a posicao fixa.
 */
const temPopover =
  typeof HTMLElement !== 'undefined' &&
  typeof HTMLElement.prototype.showPopover === 'function';

/** Distancia minima do balao ate a borda da janela, e dele ate o botao. */
const MARGEM_DA_JANELA = 8;
const DISTANCIA_DO_BOTAO = 4;

/**
 * Posiciona o balao junto do rotulo, dentro da janela: abaixo e alinhado a esquerda
 * do rotulo; acima, quando nao cabe embaixo; puxado para a esquerda, quando passaria
 * da borda direita.
 */
function posicionar(balao: HTMLElement, ancora: HTMLElement): void {
  const rotulo = ancora.getBoundingClientRect();
  const largura = balao.offsetWidth;
  const altura = balao.offsetHeight;
  const janelaLargura = document.documentElement.clientWidth || window.innerWidth;
  const janelaAltura = document.documentElement.clientHeight || window.innerHeight;

  let topo = rotulo.bottom + DISTANCIA_DO_BOTAO;
  const acima = rotulo.top - DISTANCIA_DO_BOTAO - altura;
  if (topo + altura > janelaAltura - MARGEM_DA_JANELA && acima >= MARGEM_DA_JANELA) {
    topo = acima;
  }
  let esquerda = rotulo.left;
  if (esquerda + largura > janelaLargura - MARGEM_DA_JANELA) {
    esquerda = Math.max(MARGEM_DA_JANELA, janelaLargura - MARGEM_DA_JANELA - largura);
  }
  balao.style.top = `${Math.round(topo)}px`;
  balao.style.left = `${Math.round(esquerda)}px`;
}

/**
 * O botao (i) e o balao da ajuda.
 *
 * O balao fica fora do fluxo da pagina: e um `popover` manual, que o navegador desenha
 * na camada de cima, com posicao fixa calculada pelo rotulo. Antes ele era absoluto
 * dentro do rotulo, e dentro de uma tabela com rolagem (o cabecalho com (i) dos
 * Relatorios) aumentava a area rolavel e criava a barra horizontal. Na camada de cima
 * ele nao muda o tamanho nem a rolagem de nada, e fica tambem por cima de janela modal.
 * O navegador sem popover desenha o mesmo balao com a posicao fixa.
 *
 * Abre e fecha pelo botao, Esc fecha, clique fora fecha, e rolar ou redimensionar a
 * janela fecha: o balao parado num lugar enquanto o rotulo sai dele apontaria para o
 * campo errado.
 */
function ComAjuda({ rotulo, label, texto, emLinha = false }: ComAjudaProps) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLSpanElement>(null);
  const balao = useRef<HTMLSpanElement>(null);
  const textoId = `${useId()}-ajuda`;

  // Clique fora fecha a ajuda, como qualquer balao: deixada aberta, ela cobriria o
  // campo de baixo. O clique no proprio balao nao conta como fora.
  useEffect(() => {
    if (!aberto) return;
    const fora = (event: MouseEvent) => {
      const alvo = event.target as Node;
      if (caixa.current?.contains(alvo) || balao.current?.contains(alvo)) return;
      setAberto(false);
    };
    const fechar = () => setAberto(false);
    document.addEventListener('mousedown', fora);
    // A rolagem de qualquer area (captura) e a mudanca de tamanho da janela fecham.
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    return () => {
      document.removeEventListener('mousedown', fora);
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [aberto]);

  // Antes de pintar: sobe o balao para a camada de cima e o coloca junto do rotulo.
  useLayoutEffect(() => {
    const elemento = balao.current;
    const ancora = caixa.current;
    if (!aberto || !elemento || !ancora) return;
    if (temPopover) {
      try {
        elemento.showPopover();
      } catch {
        // Ja aberto, ou fora do documento: a posicao fixa abaixo basta.
      }
    }
    posicionar(elemento, ancora);
  }, [aberto]);

  return (
    <span
      ref={caixa}
      className={cn(
        'relative items-center gap-1.5',
        emLinha ? 'inline-flex max-w-full' : 'flex',
      )}
    >
      {rotulo}
      {/* O nome do botao vem do texto escondido, e nao de aria-label: aria-label
          tambem e rotulo, e a busca do campo pelo proprio rotulo passaria a achar
          dois elementos, o campo e o botao. */}
      <button
        type="button"
        aria-expanded={aberto}
        aria-controls={textoId}
        onClick={() => setAberto((atual) => !atual)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setAberto(false);
        }}
        className={cn(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          'text-[10px] leading-none font-bold transition-colors duration-(--duration-fast)',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30',
          aberto
            ? 'border-brand-500 bg-brand-50 text-brand-600'
            : 'border-ink-300 text-ink-500 hover:border-brand-500 hover:bg-brand-50 hover:text-brand-600',
        )}
      >
        <span aria-hidden="true">i</span>
        <span className="sr-only">Sobre o campo {label}</span>
      </button>
      {aberto ? (
        <span
          ref={balao}
          id={textoId}
          role="note"
          popover={temPopover ? 'manual' : undefined}
          className={cn(
            // O popover do navegador vem centrado na tela, com margem automatica: a
            // posicao e a do rotulo, escrita por `posicionar`.
            'fixed inset-auto m-0 block w-72 max-w-[calc(100vw-1rem)] rounded-lg border',
            'border-line bg-surface p-3 text-left text-xs leading-relaxed font-normal',
            'tracking-normal whitespace-normal text-ink-700 normal-case shadow-(--shadow-lg)',
          )}
        >
          {texto}
        </span>
      ) : null}
    </span>
  );
}
